import type { FileUIPart, ToolUIPart, UIMessage } from 'ai';

import type { ActionSpec, ThinkingStep } from '../types';

type MessagePart = UIMessage['parts'][number];

const isThinkingPart = (part: MessagePart) =>
  part.type === 'reasoning' || part.type.startsWith('tool-');

const isToolPart = (part: MessagePart): part is ToolUIPart =>
  part.type.startsWith('tool-');

const toolLabel = (type: string) =>
  type.replace(/^tool-/, '').replace(/[_-]/g, ' ');

export const thinkingSteps = (message: UIMessage): ThinkingStep[] =>
  message.parts.filter(isToolPart).map((tool, index) => ({
    id: `${message.id}-thinking-${index}`,
    label: tool.title ?? toolLabel(tool.type),
    state: tool.state,
  }));

const TERMINAL_STATES: ThinkingStep['state'][] = [
  'output-available',
  'output-error',
  'output-denied',
];

export const isFinished = (step: ThinkingStep) =>
  TERMINAL_STATES.includes(step.state);

/** Text emitted before the last tool call is inter-tool narration, not the answer. */
export const answerText = (message: UIMessage): string => {
  const lastThinkingIndex = message.parts.reduce(
    (last, part, index) => (isThinkingPart(part) ? index : last),
    -1
  );
  return message.parts
    .filter(
      (part, index): part is Extract<MessagePart, { type: 'text' }> =>
        part.type === 'text' && index > lastThinkingIndex
    )
    .map(part => part.text)
    .join('\n\n')
    .trim();
};

const MARKDOWN_CODE = /(```[\s\S]*?(?:```|$)|`[^`\n]*`)/;

/** The model glues a heading onto the previous sentence often enough to fix it here, outside code. */
export const fixHeadingGlue = (text: string) =>
  text
    .split(MARKDOWN_CODE)
    .map((chunk, index) =>
      index % 2 ? chunk : chunk.replace(/([.!?:)])\s*(#{1,6} )/g, '$1\n\n$2')
    )
    .join('');

/** The same generated file can be queued twice — one chip per url. */
export const attachments = (message: UIMessage): FileUIPart[] =>
  Array.from(
    new Map(
      message.parts
        .filter((part): part is FileUIPart => part.type === 'file')
        .map(part => [part.url, part] as const)
    ).values()
  );

type WorkpathFileMetadata = { filename?: string; title?: string };

/** Files reloaded from history carry their name in Workpath metadata, not `filename`. */
export const fileNames = (file: FileUIPart) => {
  const metadata = file.providerMetadata?.workpath as
    | WorkpathFileMetadata
    | undefined;
  return {
    title: metadata?.title,
    filename: metadata?.filename ?? file.filename,
  };
};

export const actionSuggestions = (message: UIMessage): ActionSpec[] =>
  Array.from(
    new Map(
      message.parts
        .filter(part => part.type === 'data-actions')
        .flatMap(part => (part as { data: ActionSpec[] }).data)
        .map(action => [action.payload, action] as const)
    ).values()
  );

/**
 * Which suggestion the user picked at each turn: the text of the user message that
 * followed it. Resolved for the whole thread in one backward pass — the alternative
 * is a fresh scan per rendered message on every streamed token.
 */
export const chosenActions = (messages: UIMessage[]): (string | null)[] => {
  const chosen: (string | null)[] = [];
  let nextUserText: string | null = null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    chosen[index] = nextUserText;
    if (messages[index].role === 'user') {
      nextUserText = answerText(messages[index]);
    }
  }
  return chosen;
};
